// getShouldShowLiveStreamBalanceWarning.js
'use strict';

import { CHARGE_PERIOD_SECONDS } from '../resource/liveStreamConstants.js';

/**
 * Decide whether the exclusive private (1-1) balance warning should be visible.
 * The warning only surfaces within the final `remainingBalanceMultiple` charge
 * periods before the balance runs out. The threshold is measured in charge
 * periods (`CHARGE_PERIOD_SECONDS`) to stay consistent with how the run-out
 * timestamp is derived.
 * @param {object} params
 * @param {?number} params.exclusivePrivateRunOutAt - unix timestamp (seconds) when the balance runs out, or null when there is no ongoing 1-1.
 * @param {number} params.remainingBalanceMultiple - number of charge periods before run-out to start warning. 0 disables the threshold.
 * @param {number} params.now - current unix timestamp in seconds.
 * @returns {boolean} true when the warning should be visible.
 * @example
 * getShouldShowLiveStreamBalanceWarning({
 *   exclusivePrivateRunOutAt: 1700000100,
 *   remainingBalanceMultiple: 3,
 *   now: 1700000000,
 * });
 * // => true (100s left, within the 180s threshold)
 */
const getShouldShowLiveStreamBalanceWarning = ({
  exclusivePrivateRunOutAt,
  remainingBalanceMultiple = 0,
  now,
}) => {
  if (!exclusivePrivateRunOutAt) return false;

  const remainingSeconds = Math.max(exclusivePrivateRunOutAt - now, 0);
  const thresholdSeconds = remainingBalanceMultiple * CHARGE_PERIOD_SECONDS;

  // No valid warning window (config missing, still loading, or set to 0) -> stay
  // hidden. `!(thresholdSeconds > 0)` also covers NaN from an undefined config.
  if (!(thresholdSeconds > 0)) return false;

  // Only warn within the final few charge periods (e.g. 3 = remainingBalanceMultiple).
  if (remainingSeconds > thresholdSeconds) return false;

  return true;
};

export default getShouldShowLiveStreamBalanceWarning;
